import { ValueType } from '@srm/ui/types/ValueType';
import { toDateOrNull } from '@srm/ui/lib/dateFormValue';
import { VALIDATION_MESSAGES, DATE_MESSAGE_FORMAT, DATETIME_MESSAGE_FORMAT } from './messages';
import { getDocumentRequiredMessage, type IRequiredMessageField } from './requiredMessage';

/**
 * Layer 1 валидации поля документа — ЧИСТАЯ синхронная функция.
 *
 * Воспроизводит 1:1 правила, которые сейчас собираются в
 * `Field.tsx` (`fieldControlType`, `checkDate`) и исполняются встроенным
 * движком react-hook-form. Все формульные значения (видимость, required,
 * границы дат) уже ВЫЧИСЛЕНЫ вызывающим слоем (Layer 2 / resolver) и
 * передаются сюда готовыми — здесь нет ни React, ни FormulaManager, ни сети.
 *
 * Семантика сверена с исходником RHF 7.74.0
 * (`node_modules/react-hook-form/dist/index.esm.mjs`, `validateField`):
 *  - порядок firstError: required → maxLength/minLength → pattern → validate(notWhitespaceOnly);
 *  - `maxLength`/`minLength` и `pattern` применяются ТОЛЬКО к непустой строке
 *    (пустые/нестроковые значения их пропускают — отсюда защитный `(^$)|…` в схемах);
 *  - лимит длины строки берётся из `max` и `maxLength` схемы по минимуму (см. resolveMaxLength);
 *  - required считает пустым: '', пустой массив, null/undefined, булев `false`,
 *    но НЕ число 0 (поэтому required-чекбокс со значением 0 проходит);
 *  - числовые min/max в Field.tsx уходят в виджет (InputNumberControl), а НЕ в RHF rules,
 *    поэтому здесь намеренно НЕ валидируются (иначе разъедется паритет);
 *  - даты валидируются только через `checkDate` (required/length/pattern к ним не применяются).
 */

export interface IDebugValidationFlags {
    skipAllValidation?: boolean;
    skipRequiredValidation?: boolean;
    skipPatternValidation?: boolean;
    skipLengthValidation?: boolean;
}

export interface IFieldValidationInput extends IRequiredMessageField {
    valueType: ValueType;
    /** Текущее значение поля из формы */
    value: unknown;
    /** Видимость, вычисленная из visibilityRules. Невидимое поле не валидируется. */
    visible: boolean;
    /** required, вычисленный из формулы required */
    required: boolean;
    /** field.pattern (СЫРОЙ атрибут схемы) */
    pattern?: string;
    /** field.min (СЫРОЙ атрибут схемы): для строк — длина (Number), для дат — флаг применения */
    min?: string;
    /** field.max (СЫРОЙ атрибут схемы): для строк — длина (Number), для дат — флаг применения */
    max?: string;
    /** Активное настроенное ограничение maxLength; undefined означает отсутствие активного значения. */
    maxLength?: number | null;
    /** maxLengthRules вычислено в false: не применять также дефолтный лимит Text в 255 символов. */
    maxLengthDisabledByRule?: boolean;
    /** Граница min, вычисленная из формулы (epoch ms / parseable). Только для дат. */
    minVal?: string | number | null;
    /** Граница max, вычисленная из формулы (epoch ms / parseable). Только для дат. */
    maxVal?: string | number | null;
    /** workCalendarRules вычислен в «активно» */
    workCalendarActive?: boolean;
    /** Проверка выходного дня (из рабочего календаря) */
    isHoliday?: (date: Date) => boolean;
    debug?: IDebugValidationFlags;
}

/** Типы, к которым применяется длина строки (Field.tsx minLength/maxLength). */
const LENGTH_TYPES: ReadonlySet<ValueType> = new Set([ValueType.Text, ValueType.LongText, ValueType.Url]);

/** Типы, для которых required-строка проверяется на «только пробелы» (Field.tsx notWhitespaceOnly). */
const WHITESPACE_TYPES: ReadonlySet<ValueType> = new Set([
    ValueType.Text,
    ValueType.LongText,
    ValueType.Url,
    ValueType.Base64Text,
]);

/** Типы, валидируемые через checkDate. NB: Year сюда НЕ входит (использует базовые rules). */
const DATE_TYPES: ReadonlySet<ValueType> = new Set([ValueType.Date, ValueType.DateTime, ValueType.NoSecDateTime]);

/** Валидируется ли тип через checkDate. */
export function isDateValueType(valueType: ValueType): boolean {
    return DATE_TYPES.has(valueType);
}

/**
 * Эффективный лимит длины строки по схеме поля.
 *
 * Конфигурация задаёт его двумя атрибутами: `max` — правило валидации, `maxLength` —
 * ограничение ввода на контроле. Значение, пришедшее из формулы, каскада `changes` или
 * справочника, ввод минует, поэтому валидируем по минимуму из обоих. У `Text` без `max`
 * остаётся исторический дефолт 255 — кроме случая, когда `maxLengthRules` сняло ограничение
 * (`maxLengthDisabledByRule`).
 */
export function resolveMaxLength(
    valueType?: ValueType,
    max?: string,
    maxLength?: number,
    maxLengthDisabledByRule?: boolean,
): number | undefined {
    if (valueType === undefined || !LENGTH_TYPES.has(valueType)) {
        return undefined;
    }

    const limits: number[] = [];
    if (max) {
        limits.push(Number(max));
    }
    if (maxLength) {
        limits.push(Number(maxLength));
    }
    // Исторический дефолт Text — только когда ограничение не задано вовсе: явный `maxLength`
    // (как и `max`) настроен осознанно и 255 символами его ужимать нельзя.
    if (limits.length === 0 && max === undefined && valueType === ValueType.Text && !maxLengthDisabledByRule) {
        limits.push(255);
    }

    return limits.length > 0 ? Math.min(...limits) : undefined;
}

/** Пусто для required (зеркало RHF validateField). */
export function isRequiredEmpty(value: unknown): boolean {
    return (
        value === '' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'boolean' && value === false)
    );
}

/** Непустая строка (условие применения length/pattern в RHF: !isEmpty && isString). */
function isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value !== '';
}

/**
 * Главная точка входа: вернуть текст первой ошибки поля или undefined, если поле валидно.
 * В режиме просмотра (не edit/new) вызывающий слой не должен вызывать эту функцию.
 */
export function evaluateFieldErrors(input: IFieldValidationInput): string | undefined {
    if (!input.visible) {
        return undefined;
    }
    if (DATE_TYPES.has(input.valueType)) {
        return checkDate(input);
    }
    return checkBaseRules(input);
}

/** Базовые правила для всех нефинальных типов (строки/числа/boolean/year/yesno). */
function checkBaseRules(input: IFieldValidationInput): string | undefined {
    const { valueType, value, required, pattern, min, max, maxLength, maxLengthDisabledByRule, debug = {} } = input;

    const skipReq = !!debug.skipAllValidation || !!debug.skipRequiredValidation;
    const skipPat = !!debug.skipAllValidation || !!debug.skipPatternValidation;
    const skipLen = !!debug.skipAllValidation || !!debug.skipLengthValidation;
    const effectiveRequired = skipReq ? false : required;

    // 1) required
    if (effectiveRequired && isRequiredEmpty(value)) {
        return getDocumentRequiredMessage(input);
    }

    // 2) длина строки (только непустая строка, типы LENGTH_TYPES)
    if (!skipLen && LENGTH_TYPES.has(valueType) && isNonEmptyString(value)) {
        const minLen = min ? Number(min) : undefined;
        const maxLen = resolveMaxLength(valueType, max, maxLength ?? undefined, maxLengthDisabledByRule);
        // RHF: exceedMax имеет приоритет в сообщении
        if (maxLen !== undefined && value.length > maxLen) {
            return VALIDATION_MESSAGES.maxLength(maxLen);
        }
        if (minLen !== undefined && value.length < minLen) {
            return VALIDATION_MESSAGES.minLength(minLen);
        }
    }

    // 3) pattern (только непустая строка, не Url)
    if (!skipPat && pattern && valueType !== ValueType.Url && isNonEmptyString(value)) {
        // как в Field.tsx: new RegExp(field.pattern, 'g'); RHF использует value.match(regex).
        // Битый pattern из конфига — fail-open: SyntaxError здесь единственный синхронный throw
        // внутри прогона, без catch он тихо валит ВЕСЬ resolver (submit перестаёт работать).
        try {
            const regex = new RegExp(pattern, 'g');
            if (!value.match(regex)) {
                return VALIDATION_MESSAGES.patternMismatch;
            }
        } catch (error) {
            console.error(`Невалидный pattern "${pattern}" в схеме документа:`, error);
        }
    }

    // 4) validate.notWhitespaceOnly — присутствует только если required && строковый тип
    if (effectiveRequired && WHITESPACE_TYPES.has(valueType) && typeof value === 'string' && value.trim() === '') {
        return getDocumentRequiredMessage(input);
    }

    return undefined;
}

/** Порт checkDate из Field.tsx (Date/DateTime/NoSecDateTime). */
function checkDate(input: IFieldValidationInput): string | undefined {
    const { valueType, value, required, min, max, minVal, maxVal, workCalendarActive, isHoliday, debug = {} } = input;

    if (debug.skipAllValidation) {
        return undefined;
    }
    const skipReq = !!debug.skipRequiredValidation;
    if (!skipReq && required && (value === undefined || value === null || value === '')) {
        return getDocumentRequiredMessage(input);
    }

    try {
        const isDateOnly = valueType === ValueType.Date;
        const fmt = isDateOnly ? DATE_MESSAGE_FORMAT : DATETIME_MESSAGE_FORMAT;
        const hasValue = value !== undefined && value !== null && value !== '';

        const source = toDateOrNull(value);
        if (!source) return undefined;
        const val = new Date(source.getTime());
        val.setSeconds(0);
        val.setMilliseconds(0);
        if (isDateOnly) {
            val.setHours(0);
            val.setMinutes(0);
        }

        if (max && hasValue) {
            const valMax = new Date(+(maxVal as never));
            valMax.setSeconds(0);
            valMax.setMilliseconds(0);
            if (isDateOnly) {
                valMax.setHours(0);
                valMax.setMinutes(0);
            }
            if (val.getTime() > valMax.getTime()) {
                return VALIDATION_MESSAGES.maxDate(valMax, fmt);
            }
        }

        if (min && hasValue) {
            const valMin = new Date(+(minVal as never));
            valMin.setSeconds(0);
            valMin.setMilliseconds(0);
            if (isDateOnly) {
                valMin.setHours(0);
                valMin.setMinutes(0);
            }
            if (val.getTime() < valMin.getTime()) {
                return VALIDATION_MESSAGES.minDate(valMin, fmt);
            }
        }

        if (workCalendarActive && isHoliday?.(val)) {
            return VALIDATION_MESSAGES.holiday;
        }

        return undefined;
    } catch {
        // как в Field.tsx: на любой сбой парсинга — поле считается валидным
        return undefined;
    }
}
